import { usePage } from "@inertiajs/react";
import { Head } from "@inertiajs/react";
import iconTransparent from "@/assets/icons/icon_transparent.svg";
import { ButtonAnchor } from "@/components/ui/Button";
import type { SharedProps } from "@/types";

const STEPS = [
  { key: "link_hackatime", label: "Link Hackatime" },
  { key: "go_to_projects", label: "Create a project" },
  { key: "finish", label: "Finish!" },
];

function ProgressCircle({ state }: { state: "done" | "current" | "upcoming" }) {
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center sm:h-16 sm:w-16">
      {state === "current" ? (
        <div className="bg-goldenrod border-line flex h-full w-full items-center justify-center rounded-full border-2">
          <div className="border-line bg-surface-plain h-7 w-7 rounded-full border-2" />
        </div>
      ) : (
        <div
          className={`border-line h-10 w-10 rounded-full border-2 ${state === "done" ? "bg-goldenrod" : "bg-surface-alt"}`}
        />
      )}
    </div>
  );
}

const ALL_STEPS = [{ key: "log_in", label: "Log in" }, ...STEPS];

function ProgressBar({ currentStep }: { currentStep: string }) {
  const currentIndex = STEPS.findIndex((s) => s.key === currentStep);
  const completedCount = currentIndex + 1;
  const progressPercent = (completedCount / STEPS.length) * 100;

  return (
    <div className="relative flex w-full max-w-[700px] items-start justify-between">
      <div className="absolute top-7 right-6 left-6 h-3 -translate-y-1/2 sm:top-8">
        <div className="bg-surface-alt border-line h-full w-full border-2" />
        <div
          className="bg-goldenrod absolute top-0 left-0 h-full transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {ALL_STEPS.map((step, i) => {
        const stepIndex = i - 1;
        const state: "done" | "current" | "upcoming" =
          i === 0
            ? "done"
            : stepIndex < currentIndex
              ? "done"
              : stepIndex === currentIndex
                ? "current"
                : "upcoming";

        return (
          <div
            key={step.key}
            className="relative z-10 flex flex-col items-center gap-2"
          >
            <ProgressCircle state={state} />
            <span
              className={`font-display text-caption text-ink sm:text-body text-center ${state === "current" ? "font-bold" : ""}`}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function Onboarding() {
  const { props } = usePage<SharedProps>();

  return (
    <>
      <Head>
        <title>{`${props.program.name} - Onboarding`}</title>
      </Head>
      <div className="bg-bg flex min-h-screen flex-col items-center gap-16 px-6 py-16 sm:py-24">
        <ProgressBar currentStep="link_hackatime" />

        <div className="flex w-full max-w-2xl flex-col items-center gap-10">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <img src={iconTransparent} alt="" className="h-14 w-auto" />
            <h1 className="font-display text-title text-ink text-center font-bold">
              Welcome to {props.program.name}
            </h1>
          </div>

          <div className="text-body text-ink-muted flex flex-col gap-4 text-center">
            <p>
              Every hour you spend building, and get approved, adds{" "}
              <span className="text-ink font-bold">
                ${(props.program.budget_per_hour_cents / 100).toFixed(2)}
              </span>{" "}
              to your event's budget - on top of what your RSVPs earn.
            </p>
            <p>
              Hackatime is how those hours get counted. Link it to your{" "}
              {props.program.name} account and it tracks your time in your
              editor, or on a hardware project, automatically.
            </p>
          </div>

          <ButtonAnchor href="/hackatime/link" size="lg">
            Connect Hackatime
          </ButtonAnchor>
        </div>
      </div>
    </>
  );
}
